/*
 * Copyright (c) 2026 Centrica.  All rights reserved.
 *
 * This source code is licensed under the license found in the
 * LICENSE file in the root directory of this source tree.
 * Date: 2026-10-22
 */
import {
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  OnDestroy,
  computed,
  effect,
  inject,
  signal,
  untracked,
  viewChild,
} from '@angular/core';
import { Subscription } from 'rxjs';
import lottie, { AnimationItem } from 'lottie-web';
import { IconComponent, IconName } from 'nebula-ui-kit';
import {
  IA_FALLOS_QUE_DESHABILITAN,
  IA_TOPE_PREGUNTA,
  IaCuotaResponse,
  IaFallo,
  IaMensaje,
  IaService,
  LAYOUT,
  MenuIconService,
  TranslatePipe,
  ThemeService,
  TranslationService,
  iaFalloDe,
} from '@nebula-shared/data-access';

const I18N = LAYOUT.NEBULINA;

/**
 * Las tres animaciones del lanzador (presentacion «Animaciones de Nebulina», 29-oct-2026):
 * <ul>
 *   <li>`flotante`: con el modulo elegido y sin opcion del menu; se reproduce tres veces y queda
 *   quieta.</li>
 *   <li>`ayuda` («¿En qué puedo ayudarte?»): sin conversacion; se reproduce al aparecer y luego
 *   cada 3 a 5 minutos.</li>
 *   <li>`aqui` («Estoy aquí para ti»): con la conversacion minimizada; mismo ritmo que `ayuda`.</li>
 * </ul>
 */
export type VarianteNebulina = 'flotante' | 'ayuda' | 'aqui';

/** Lottie de cada variante por tema, servidos desde los assets del shell; van tal cual se entregaron. */
export const NEBULINA_ANIMACIONES: Record<VarianteNebulina, { claro: string; oscuro: string }> = {
  flotante: {
    claro: 'assets/nebulina/nebulina-flotante.json',
    oscuro: 'assets/nebulina/nebulina-flotante-oscuro.json',
  },
  ayuda: {
    claro: 'assets/nebulina/nebulina-ayuda.json',
    oscuro: 'assets/nebulina/nebulina-ayuda-oscuro.json',
  },
  aqui: {
    claro: 'assets/nebulina/nebulina-aqui.json',
    oscuro: 'assets/nebulina/nebulina-aqui-oscuro.json',
  },
};
/** Avatar del panel por tema: la cara de Nebulina sacada del Lottie, con fondo transparente. */
export const NEBULINA_AVATAR = 'assets/nebulina/nebulina-avatar-claro.svg';
export const NEBULINA_AVATAR_OSCURO = 'assets/nebulina/nebulina-avatar-oscuro.svg';

/** Capa de la barra con el recuadro y su texto en español: se oculta y el recuadro va en HTML (i18n). */
export const NEBULINA_CAPA_DEL_RECUADRO = 'Cuadro de texto';
/** Composicion de la burbuja de `flotante` y sus dos capas de texto dibujado en español. */
export const NEBULINA_CAPA_DE_LA_BURBUJA = 'Burbuja';
const CAPAS_DE_TEXTO_DE_LA_BURBUJA: Record<string, string> = {
  'Shape Layer 1': 'nebulina-burbuja-pregunta',
  'Shape Layer 2': 'nebulina-burbuja-escribiendo',
};
/** Texto que se pinta en cada capa de la burbuja, por el id que se le pone a la capa. */
export const NEBULINA_TEXTOS_DE_LA_BURBUJA: Record<string, string> = {
  'nebulina-burbuja-pregunta': I18N.BUBBLE,
  'nebulina-burbuja-escribiendo': I18N.BUBBLE_TYPING,
};

/** `flotante` se reproduce tres veces y queda quieta. */
export const NEBULINA_REPRODUCCIONES_FLOTANTE = 3;
/** `ayuda` y `aqui` vuelven a reproducirse entre 3 y 5 minutos despues. */
export const NEBULINA_PAUSA_MINIMA_MS = 3 * 60_000;
export const NEBULINA_PAUSA_MAXIMA_MS = 5 * 60_000;

/** Datos minimos del Lottie que se leen aqui. */
interface CapaLottie {
  nm?: string;
  hd?: boolean;
  ln?: string;
}
interface DatosLottie {
  layers?: CapaLottie[];
  assets?: { nm?: string; layers?: CapaLottie[] }[];
}

/**
 * La variante del lanzador para el estado de Nebulina.
 *
 * @param conConversacion si hay un dialogo iniciado: mensajes guardados o texto escrito en la caja
 * @param opcion nombre de la opcion del menu que se esta viendo; vacio si aun no se elige ninguna
 * @returns la variante que corresponde
 */
export function varianteDelLanzador(conConversacion: boolean, opcion: string): VarianteNebulina {
  if (conConversacion) return 'aqui';
  return opcion ? 'ayuda' : 'flotante';
}

/**
 * La animacion de una variante en el tema.
 *
 * @param variante la variante del lanzador
 * @param oscuro si la aplicacion se ve oscura
 * @returns la ruta del Lottie en los assets
 */
export function animacionDe(variante: VarianteNebulina, oscuro: boolean): string {
  const rutas = NEBULINA_ANIMACIONES[variante];
  return oscuro ? rutas.oscuro : rutas.claro;
}

/**
 * El avatar que corresponde al tema.
 *
 * @param oscuro si la aplicacion se ve oscura
 * @returns la ruta del SVG en los assets
 */
export function avatarDelTema(oscuro: boolean): string {
  return oscuro ? NEBULINA_AVATAR_OSCURO : NEBULINA_AVATAR;
}

/**
 * Pausa hasta la siguiente reproduccion de `ayuda` o `aqui`: al azar entre 3 y 5 minutos.
 *
 * @param azar fuente de azar entre 0 y 1
 * @returns los milisegundos de la pausa
 */
export function pausaEntreReproducciones(azar: () => number = Math.random): number {
  return NEBULINA_PAUSA_MINIMA_MS + azar() * (NEBULINA_PAUSA_MAXIMA_MS - NEBULINA_PAUSA_MINIMA_MS);
}

/**
 * Copia de la animacion sin el texto dibujado en español, que no cambiaria con el idioma. El
 * archivo aprobado no se toca.
 * <ul>
 *   <li>Barra (`ayuda`, `aqui`): se oculta la capa del recuadro, que va en HTML.</li>
 *   <li>Burbuja (`flotante`): sus capas de texto reciben un id para pintar ahi el texto traducido,
 *   de modo que siga el movimiento y el desvanecido de la burbuja.</li>
 * </ul>
 */
export function sinTextoDibujado<T extends DatosLottie>(datos: T): T {
  const copia = structuredClone(datos);
  for (const capa of copia.layers ?? []) {
    if (capa.nm === NEBULINA_CAPA_DEL_RECUADRO) capa.hd = true;
  }
  for (const composicion of copia.assets ?? []) {
    if (composicion.nm !== NEBULINA_CAPA_DE_LA_BURBUJA) continue;
    for (const capa of composicion.layers ?? []) {
      const id = capa.nm ? CAPAS_DE_TEXTO_DE_LA_BURBUJA[capa.nm] : undefined;
      if (id) capa.ln = id;
    }
  }
  return copia;
}

/**
 * Parte el texto de la burbuja en lineas: una si es corto, dos partidas en el espacio mas cercano
 * a la mitad si no (como «¿En qué puedo / ayudarte?» en la animacion).
 *
 * @param texto el texto traducido
 * @returns las lineas a pintar
 */
export function lineasDeLaBurbuja(texto: string): string[] {
  const limpio = texto.trim();
  if (limpio.length <= 14) return [limpio];
  const mitad = limpio.length / 2;
  let corte = -1;
  for (let i = 0; i < limpio.length; i++) {
    if (limpio[i] === ' ' && (corte < 0 || Math.abs(i - mitad) < Math.abs(corte - mitad)))
      corte = i;
  }
  return corte < 0 ? [limpio] : [limpio.slice(0, corte), limpio.slice(corte + 1)];
}

/**
 * Pinta el texto traducido en una capa de texto de la burbuja: oculta lo dibujado y pone un
 * `<text>` en el mismo lugar, dentro de la capa, que hereda su posicion, giro y opacidad.
 *
 * @param capa el grupo SVG de la capa (lleva el id de {@link sinTextoDibujado})
 * @param texto el texto traducido
 * @param tamano alto de letra en las unidades de la capa
 * @return `false` si la capa aun no se dibuja (lottie la oculta fuera de su tramo y su caja mide 0)
 */
export function pintarTextoEnLaCapa(capa: SVGGElement, texto: string, tamano: number): boolean {
  const NS = 'http://www.w3.org/2000/svg';
  let nodo = capa.querySelector<SVGTextElement>('text[data-nebulina]');
  if (!nodo) {
    const caja = capa.getBBox();
    if (!caja.width || !caja.height) return false;
    const color = capa.querySelector('path')?.getAttribute('fill') || '#0e58a9';
    for (const hijo of Array.from(capa.children)) {
      (hijo as SVGElement).style.visibility = 'hidden';
    }
    nodo = document.createElementNS(NS, 'text');
    nodo.setAttribute('data-nebulina', '');
    nodo.setAttribute('x', String(caja.x + caja.width / 2));
    nodo.setAttribute('data-centro', String(caja.y + caja.height / 2));
    nodo.setAttribute('text-anchor', 'middle');
    nodo.setAttribute('fill', color);
    nodo.style.fontFamily = "'Open Sans', system-ui, sans-serif";
    nodo.style.fontWeight = '600';
    nodo.style.visibility = 'visible';
    capa.appendChild(nodo);
  }
  const lineas = lineasDeLaBurbuja(texto);
  const centro = Number(nodo.getAttribute('data-centro'));
  const alto = tamano * 1.2;
  nodo.style.fontSize = `${tamano}px`;
  nodo.replaceChildren(
    ...lineas.map((linea, i) => {
      const tramo = document.createElementNS(NS, 'tspan');
      tramo.setAttribute('x', nodo.getAttribute('x') ?? '0');
      tramo.setAttribute(
        'y',
        String(centro + (i - (lineas.length - 1) / 2) * alto + tamano * 0.35),
      );
      tramo.textContent = linea;
      return tramo;
    }),
  );
  return true;
}

/**
 * Un mensaje de la conversacion. Los avisos del panel (cuando la IA no responde) guardan su clave
 * para traducirse al cambiar el idioma y no viajan en el historial.
 */
export interface MensajeNebulina {
  rol: 'usuario' | 'nebulina';
  texto: string;
  aviso?: boolean;
}

/** Mensaje de cada fallo de la IA: siempre texto del front, nunca el del servidor (R-G15). */
export const NEBULINA_MENSAJE_DEL_FALLO: Record<IaFallo, string> = {
  'cuota-agotada': I18N.ERROR_QUOTA,
  'sin-configuracion': I18N.ERROR_NOT_CONFIGURED,
  'clave-sin-registrar': I18N.ERROR_KEY_MISSING,
  'no-disponible': I18N.ERROR_UNAVAILABLE,
  'clave-rechazada': I18N.ERROR_KEY_REJECTED,
  'tiempo-agotado': I18N.ERROR_TIMEOUT,
  error: I18N.ERROR_GENERIC,
};

/**
 * Por que la cuota no deja preguntar, o nulo si deja.
 *
 * @param cuota la cuota del tenant
 * @returns la clave del mensaje con que se deshabilita el panel
 */
export function bloqueoPorCuota(cuota: IaCuotaResponse): string | null {
  if (cuota.claveConfigurada === 'N') return I18N.ERROR_KEY_MISSING;
  if (!cuota.activa) return I18N.ERROR_DISABLED;
  if (cuota.agotada) return I18N.ERROR_QUOTA;
  return null;
}

/**
 * Historial que se envia: los turnos del panel sin los avisos, con el rol del contrato.
 *
 * @param mensajes la conversacion del panel
 * @returns los turnos para la IA
 */
export function historialParaLaIa(mensajes: readonly MensajeNebulina[]): IaMensaje[] {
  return mensajes
    .filter((m) => !m.aviso)
    .map(
      (m): IaMensaje => ({
        rol: m.rol === 'usuario' ? 'user' : 'assistant',
        contenido: m.texto,
      }),
    );
}

/** Sugerencias de la bienvenida: cada una se envia como pregunta con su texto traducido. */
const SUGERENCIAS: readonly { icono: IconName; clave: string }[] = [
  { icono: 'CircleQuestionMark', clave: I18N.SUGGESTION_SCREEN },
  { icono: 'Search', clave: I18N.SUGGESTION_SEARCH },
  { icono: 'Download', clave: I18N.SUGGESTION_EXPORT },
];

/**
 * Nebulina en el area de trabajo: el lanzador animado abajo a la derecha y, al pulsarlo, el panel
 * de conversacion pegado a la derecha.
 *
 * <p>Pregunta a la IA del tenant (nebula-tasks) con el historial del panel y, si el contexto esta
 * puesto, el `ai_context` traducido y el nombre de la opcion abierta, que publica el menu. Al
 * abrir consulta la cuota: agotada, desactivada o sin clave, el panel no deja preguntar y lo
 * dice.</p>
 */
@Component({
  selector: 'app-nebulina',
  templateUrl: './nebulina.component.html',
  styleUrls: ['./nebulina.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [IconComponent, TranslatePipe],
})
export class NebulinaComponent implements OnDestroy {
  private readonly menuIcon = inject(MenuIconService);
  private readonly translation = inject(TranslationService);
  private readonly ia = inject(IaService);
  private readonly tema = inject(ThemeService);

  readonly i18n = I18N;
  readonly sugerencias = SUGERENCIAS;
  readonly topePregunta = IA_TOPE_PREGUNTA;

  readonly abierto = signal(false);
  readonly mensajes = signal<MensajeNebulina[]>([]);
  readonly texto = signal('');
  readonly pensando = signal(false);
  readonly conContexto = signal(true);
  readonly enfocado = signal(false);
  /** Clave del mensaje con que el panel no deja preguntar; nula si deja. */
  readonly bloqueo = signal<string | null>(null);

  /** Nombre de la opcion abierta, ya traducido por el menu. */
  readonly opcion = this.menuIcon.getOptionName();
  /** Clave i18n del `ai_context` de la opcion abierta, que publica el menu. */
  private readonly claveContexto = this.menuIcon.getOptionAiContext();

  /** Animacion del lanzador segun el estado: sin opcion, sin conversacion o minimizada con ella. */
  readonly variante = computed(() =>
    varianteDelLanzador(this.mensajes().length > 0 || !!this.texto().trim(), this.opcion()),
  );
  /** El avatar sigue al tema, como la animacion. */
  readonly avatar = computed(() => avatarDelTema(this.tema.oscuro()));
  readonly bienvenida = computed(() => this.mensajes().length === 0 && !this.pensando());
  readonly lleno = computed(() => !!this.texto().trim() && !this.bloqueo());
  readonly contextoVisible = computed(() => this.conContexto() && !!this.opcion());

  private readonly contenedor = viewChild<ElementRef<HTMLDivElement>>('animacion');
  private readonly caja = viewChild<ElementRef<HTMLTextAreaElement>>('caja');
  private animacion: AnimationItem | null = null;
  private espera: Subscription | null = null;
  /** Temporizador de la siguiente reproduccion de `ayuda` o `aqui`. */
  private pausa: ReturnType<typeof setTimeout> | null = null;
  /** Contenedor de la animacion `flotante` ya pintada, donde va el texto de la burbuja. */
  private readonly burbuja = signal<HTMLDivElement | null>(null);
  /** Alto de letra de la burbuja, medido en la capa de la pregunta antes de ocultarla. */
  private tamanoBurbuja: number | null = null;

  /**
   * La animacion se monta cada vez que el lanzador vuelve a pintarse, cada vez que cambia su
   * variante y cada vez que cambia el tema, sin recargar la pagina.
   */
  private readonly montarAnimacion = effect(() => {
    const contenedor = this.contenedor()?.nativeElement;
    const ruta = animacionDe(this.variante(), this.tema.oscuro());
    this.destruirAnimacion();
    if (contenedor) void this.cargarAnimacion(contenedor, ruta);
  });

  /** El texto de la burbuja se pinta al montar `flotante` y se repinta al cambiar el idioma. */
  private readonly textoDeLaBurbuja = effect(() => {
    const contenedor = this.burbuja();
    const textos = this.textosDeLaBurbuja();
    if (contenedor) this.pintarBurbuja(contenedor, textos);
  });

  /** Texto traducido de cada capa de la burbuja. */
  private textosDeLaBurbuja(): (readonly [string, string])[] {
    return Object.entries(NEBULINA_TEXTOS_DE_LA_BURBUJA).map(
      ([id, clave]) => [id, this.translation.translate(clave)] as const,
    );
  }

  private async cargarAnimacion(contenedor: HTMLDivElement, ruta: string): Promise<void> {
    try {
      const respuesta = await fetch(ruta);
      if (!respuesta.ok) return;
      const datos = sinTextoDibujado(await respuesta.json());
      // El lanzador pudo cerrarse, o la variante o el tema cambiar, mientras llegaba el archivo.
      if (this.contenedor()?.nativeElement !== contenedor) return;
      const variante = this.variante();
      if (animacionDe(variante, this.tema.oscuro()) !== ruta) return;
      const animacion = lottie.loadAnimation({
        container: contenedor,
        renderer: 'svg',
        loop: false,
        autoplay: true,
        animationData: datos,
        rendererSettings: {
          preserveAspectRatio: variante === 'flotante' ? 'xMidYMid meet' : 'xMidYMax meet',
        },
      });
      this.animacion = animacion;
      this.programarReproducciones(animacion, variante);
      if (variante === 'flotante') {
        // Cada capa de la burbuja se mide y se pinta en el primer cuadro en que ya se dibuja.
        const alCuadro = () => {
          this.burbuja.set(contenedor);
          if (
            this.pintarBurbuja(
              contenedor,
              untracked(() => this.textosDeLaBurbuja()),
            )
          ) {
            animacion.removeEventListener('enterFrame', alCuadro);
          }
        };
        animacion.addEventListener('enterFrame', alCuadro);
      }
    } catch {
      // Sin animacion el boton sigue funcionando con su nombre accesible.
    }
  }

  /**
   * Ritmo de la presentacion: `flotante` se reproduce tres veces y queda quieta; `ayuda` y `aqui`
   * se reproducen al aparecer y vuelven cada 3 a 5 minutos. Entre reproducciones queda el ultimo
   * cuadro.
   */
  private programarReproducciones(animacion: AnimationItem, variante: VarianteNebulina): void {
    let reproducciones = 1;
    animacion.addEventListener('complete', () => {
      if (variante === 'flotante') {
        if (reproducciones < NEBULINA_REPRODUCCIONES_FLOTANTE) {
          reproducciones++;
          animacion.goToAndPlay(0, true);
        }
        return;
      }
      this.pausa = setTimeout(() => {
        this.pausa = null;
        animacion.goToAndPlay(0, true);
      }, pausaEntreReproducciones());
    });
  }

  /**
   * Pone el texto traducido en las capas de texto de la burbuja, con el alto de la animacion.
   *
   * @return `true` cuando todas las capas ya tienen su texto
   */
  private pintarBurbuja(
    contenedor: HTMLDivElement,
    textos: readonly (readonly [string, string])[],
  ): boolean {
    const pregunta = contenedor.querySelector<SVGGElement>('#nebulina-burbuja-pregunta');
    if (!pregunta) return true;
    // La pregunta va en dos lineas en la animacion: su alto da el de la letra. Mientras la capa
    // no se dibuja, su caja mide 0 y se espera al siguiente cuadro.
    if (this.tamanoBurbuja === null) {
      const alto = pregunta.getBBox().height;
      if (!alto) return false;
      this.tamanoBurbuja = alto / 2.3;
    }
    let completa = true;
    for (const [id, texto] of textos) {
      const capa = contenedor.querySelector<SVGGElement>(`#${id}`);
      if (capa && !pintarTextoEnLaCapa(capa, texto, this.tamanoBurbuja)) completa = false;
    }
    return completa;
  }

  /** Al abrir se consulta la cuota; si no responde, el panel sigue y el fallo sale al preguntar. */
  abrir(): void {
    this.abierto.set(true);
    this.ia.cuota().subscribe({
      next: (cuota) => this.bloqueo.set(bloqueoPorCuota(cuota)),
      error: (error) => {
        const fallo = iaFalloDe(error);
        if (IA_FALLOS_QUE_DESHABILITAN.includes(fallo)) {
          this.bloqueo.set(NEBULINA_MENSAJE_DEL_FALLO[fallo]);
        }
      },
    });
  }

  /** Minimizar vuelve al lanzador y conserva la conversacion: sale «Estoy aquí para ti». */
  minimizar(): void {
    this.abierto.set(false);
  }

  /** Cerrar termina el dialogo: borra la conversacion y vuelve al lanzador. */
  cerrar(): void {
    this.nuevaConversacion();
    this.abierto.set(false);
  }

  nuevaConversacion(): void {
    this.cancelarEspera();
    this.mensajes.set([]);
    this.vaciarCaja();
    this.pensando.set(false);
  }

  /**
   * Deja la caja en blanco. Se borra tambien en el elemento: si la señal ya estaba vacia para
   * Angular, el enlace `[value]` no ve cambio y lo escrito quedaria en pantalla.
   */
  private vaciarCaja(): void {
    this.texto.set('');
    const caja = this.caja()?.nativeElement;
    if (caja) caja.value = '';
  }

  quitarContexto(): void {
    this.conContexto.set(false);
  }

  usarEstaPantalla(): void {
    this.conContexto.set(true);
  }

  alEscribir(evento: Event): void {
    this.texto.set((evento.target as HTMLTextAreaElement).value);
  }

  /** Enter envia; Mayus+Enter deja el salto de linea. */
  alPulsarTecla(evento: KeyboardEvent): void {
    if (evento.key === 'Enter' && !evento.shiftKey) {
      evento.preventDefault();
      this.preguntar(this.texto());
    }
  }

  enviar(): void {
    this.preguntar(this.texto());
  }

  /** La sugerencia se envia con el texto que el usuario ve, en su idioma. */
  preguntarSugerencia(clave: string): void {
    this.preguntar(this.translation.translate(clave));
  }

  preguntar(pregunta: string): void {
    const limpia = pregunta.trim().slice(0, IA_TOPE_PREGUNTA);
    if (!limpia || this.pensando() || this.bloqueo()) return;

    const historial = historialParaLaIa(this.mensajes());
    const clave = this.claveContexto();
    const conContexto = this.conContexto() && !!this.opcion();
    this.mensajes.update((ms) => [...ms, { rol: 'usuario', texto: limpia }]);
    this.vaciarCaja();
    this.pensando.set(true);
    this.espera = this.ia
      .conversar({
        pregunta: limpia,
        historial,
        aiContext: conContexto && clave ? this.translation.translate(clave) : null,
        nombreOpcion: conContexto ? this.opcion() : null,
      })
      .subscribe({
        next: (respuesta) => {
          this.espera = null;
          this.mensajes.update((ms) => [...ms, { rol: 'nebulina', texto: respuesta.respuesta }]);
          this.pensando.set(false);
        },
        error: (error) => {
          this.espera = null;
          const fallo = iaFalloDe(error);
          if (fallo === 'no-disponible') {
            this.distinguirNoDisponible();
            return;
          }
          if (IA_FALLOS_QUE_DESHABILITAN.includes(fallo)) {
            this.bloqueo.set(NEBULINA_MENSAJE_DEL_FALLO[fallo]);
          }
          this.avisar(NEBULINA_MENSAJE_DEL_FALLO[fallo]);
        },
      });
  }

  /**
   * Un 503 es la IA desactivada o el proveedor saturado: la cuota los distingue. Desactivada
   * deshabilita el panel; si no, o si la cuota tampoco responde, solo se avisa.
   */
  private distinguirNoDisponible(): void {
    const noDisponible = NEBULINA_MENSAJE_DEL_FALLO['no-disponible'];
    this.espera = this.ia.cuota().subscribe({
      next: (cuota) => {
        this.espera = null;
        if (!cuota.activa) {
          this.bloqueo.set(I18N.ERROR_DISABLED);
          this.avisar(I18N.ERROR_DISABLED);
        } else {
          this.avisar(noDisponible);
        }
      },
      error: () => {
        this.espera = null;
        this.avisar(noDisponible);
      },
    });
  }

  /** Deja en la conversacion el mensaje del front con que la IA no respondio. */
  private avisar(clave: string): void {
    this.mensajes.update((ms) => [...ms, { rol: 'nebulina', texto: clave, aviso: true }]);
    this.pensando.set(false);
  }

  detener(): void {
    this.cancelarEspera();
    this.pensando.set(false);
  }

  private cancelarEspera(): void {
    this.espera?.unsubscribe();
    this.espera = null;
  }

  private destruirAnimacion(): void {
    if (this.pausa) clearTimeout(this.pausa);
    this.pausa = null;
    this.burbuja.set(null);
    this.tamanoBurbuja = null;
    this.animacion?.destroy();
    this.animacion = null;
  }

  ngOnDestroy(): void {
    this.cancelarEspera();
    this.destruirAnimacion();
  }
}
